import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import '../Admin/Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const FormulationOrderList = forwardRef(({ onSelectOrder, selectedOrderId, onNew }, ref) => {
  const { t } = useTranslation('production');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    refresh: fetchOrders
  }));

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/formulations/orders?page_size=100');
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (e) {
      console.error('Error fetching formulation orders:', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(o => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (o.order_number || '').toLowerCase().includes(s) ||
      (o.recipe_name || '').toLowerCase().includes(s) ||
      (o.output_item_name || '').toLowerCase().includes(s)
    );
  });

  const getStatusBadge = (status) => {
    const map = {
      DRAFT: 'status-warning',
      IN_PROGRESS: 'status-active',
      COMPLETED: 'status-success',
      CANCELLED: 'status-error',
      VOIDED: 'status-error',
    };
    return <span className={`status-badge ${map[status] || 'status-inactive'}`}>{status ? t('formulationOrders.status.' + status, { defaultValue: status }) : status}</span>;
  };

  const formatCurrency = (val) => val != null ? `$${parseFloat(val).toFixed(2)}` : '-';
  const formatDate = (d) => d ? formatAppDate(d) : '-';
  const formatQty = (val) => val != null ? parseFloat(val).toFixed(2) : '-';

  if (loading && orders.length === 0) {
    return <div className="list-container"><div className="loading">{t('formulationOrderList.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>📋 {t('formulationOrderList.title')}</h2>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('formulationOrderList.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('formulationOrderList.allStatus')}</option>
            <option value="DRAFT">{t('formulationOrders.status.DRAFT')}</option>
            <option value="IN_PROGRESS">{t('formulationOrders.status.IN_PROGRESS')}</option>
            <option value="COMPLETED">{t('formulationOrders.status.COMPLETED')}</option>
            <option value="CANCELLED">{t('formulationOrders.status.CANCELLED')}</option>
            <option value="VOIDED">{t('formulationOrders.status.VOIDED')}</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('formulationOrderList.columns.orderNumber')}</th>
              <th>{t('formulationOrderList.columns.recipe')}</th>
              <th>{t('formulationOrderList.columns.output')}</th>
              <th>{t('common:ui.status')}</th>
              <th>{t('formulationOrderList.columns.qty')}</th>
              <th>{t('formulationOrderList.columns.cost')}</th>
              <th>{t('common:ui.date')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr><td colSpan="7" className="no-data">{t('formulationOrderList.empty')}</td></tr>
            ) : (
              filteredOrders.map(o => (
                <tr
                  key={o.formulation_order_id}
                  className={selectedOrderId === o.formulation_order_id ? 'selected' : ''}
                  onClick={() => onSelectOrder(o.formulation_order_id)}
                >
                  <td><strong>{o.order_number}</strong></td>
                  <td>{o.recipe_name || '-'}</td>
                  <td>{o.output_item_name || '-'}</td>
                  <td>{getStatusBadge(o.status)}</td>
                  <td>{formatQty(o.status === 'COMPLETED' ? o.actual_output_quantity : o.planned_output_quantity)}</td>
                  <td>{formatCurrency(o.total_cost)}</td>
                  <td>{formatDate(o.completed_date || o.created_date)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default FormulationOrderList;
